/**
 * Bot va WebApp'ning ikkalasida ham ishlatiladigan ko'rsatish (display) yordamchilari.
 *
 * Ming ajratkichi sifatida uzilmas bo'shliq (NBSP, U+00A0) ishlatiladi — shunda
 * "62 000" qator oxirida ikkiga bo'linib ketmaydi.
 *
 * Diqqat: belgi ataylab '\u00A0' escape shaklida yozilgan. Xom NBSP belgisi manba
 * kodda ko'rinmaydi, oddiy bo'shliqdan farq qilmaydi va eslint uni xato deb belgilaydi.
 */
import { Lang } from './enums'

const NBSP = '\u00A0'

/** Butun sonni ming xonalarga NBSP bilan ajratilgan qator qiladi (ishorasiz qism uchun). */
function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)
}

/** Sonni (yoki BigInt'ni) ming xonalarga ajratib, matn sifatida chiqaradi — masalan `12 400`. */
export function formatNumber(n: number | bigint): string {
  if (typeof n === 'bigint') {
    const isNegative = n < 0n
    const abs = isNegative ? -n : n
    return `${isNegative ? '-' : ''}${groupThousands(abs.toString())}`
  }
  const rounded = Math.round(n)
  const isNegative = rounded < 0
  const abs = isNegative ? -rounded : rounded
  return `${isNegative ? '-' : ''}${groupThousands(abs.toString())}`
}

/** Summani so'm birligi bilan ko'rsatadi — masalan `62 000 so'm`. */
export function formatSom(amount: number | bigint): string {
  return `${formatNumber(amount)}${NBSP}so'm`
}

/**
 * Summani PUL BIRLIGI TILGA MOS ravishda ko'rsatadi — masalan `62 000 so'm` (UZ)
 * yoki `62 000 сум` (RU).
 *
 * NEGA alohida funksiya kerak: `formatSom()` HAR DOIM o'zbekcha "so'm" qo'shadi,
 * tildan qat'i nazar — bu bot tarafida ruscha blogerga ketadigan deyarli barcha pul
 * xabarlarida (`check.final`, `balance.body`, `payout.done`, `card.request`,
 * `myVideos.*`) noto'g'ri natija berardi (audit topilmasi). `formatSom()`ning o'zi
 * ATAYLAB o'zgartirilmaydi — u WebApp admin panelida (faqat o'zbekcha, u yerda
 * hozirgi xatti-harakat to'g'ri) ham ishlatiladi, imzosini o'zgartirish parallel
 * ishlayotgan agentning kodini buzardi.
 *
 * Naqsh — `formatDateForLang` (`apps/server/src/bot/handlers/myVideos.ts`) bilan bir
 * xil: sana uchun bu muammo allaqachon shu tarzda hal qilingan, shu yerda pul uchun
 * takrorlanadi.
 */
export function formatSomForLang(amount: number | bigint, lang: Lang): string {
  if (lang === Lang.RU) {
    return `${formatNumber(amount)}${NBSP}сум`
  }
  return formatSom(amount)
}

/** Katta summalarni qisqartirilgan (mln) shaklda ko'rsatadi — masalan `1.2 mln so'm`. */
export function formatCompactSom(amount: number | bigint): string {
  const num = typeof amount === 'bigint' ? Number(amount) : amount
  if (Math.abs(num) >= 1_000_000) {
    const millions = Math.round((num / 1_000_000) * 10) / 10
    return `${millions}${NBSP}mln so'm`
  }
  return formatSom(amount)
}

/** Karta raqamini maskalaydi — faqat birinchi/oxirgi 4 raqam ko'rinadi: `8600 **** **** 1234`. */
export function maskCardNumber(card: string): string {
  const digits = card.replace(/\D/g, '')

  // Kutilmagan formatda ham HECH QACHON to'liq raqam qaytarilmaydi.
  // Bu funksiya himoya qatlami — noma'lum kirishda "ochib yuborish" teskari mantiq bo'lardi.
  if (digits.length < 8) return '****'

  const last4 = digits.slice(-4)
  if (digits.length !== 16) return `**** ${last4}`

  const first4 = digits.slice(0, 4)
  return `${first4} **** **** ${last4}`
}

/**
 * Video summasini hisoblaydi: `floor(views * ratePer1000 / 1000)`.
 *
 * ⚠️ Bu butun loyihadagi pul hisobining YAGONA manbasi (`docs/05-backend-schema.md`).
 * Bot, API va WebApp shu funksiyadan foydalanishi shart — boshqa joyda qayta yozilmasin.
 *
 * Ko'paytirish BO'LISHDAN OLDIN bajarilishi majburiy. `views / 1000` avval
 * hisoblansa, natija suzuvchi nuqtada aniq ifodalanmaydi va `Math.floor` butun
 * sonni bir birlikka kamaytirib yuboradi. Misol: 43 prosmotr × 5 000 so'm
 * → noto'g'ri 214, to'g'risi 215. 0–200 000 oralig'ida 13 544 ta shunday qiymat bor.
 *
 * `views` (≤ 10^8, `viewsSchema`) va stavka ko'paytmasi 2^53 dan kichik bo'lgani
 * uchun ko'paytma aniq, keyingi bitta bo'lish esa to'g'ri yaxlitlanadi.
 */
export function calculateAmount(views: number, ratePer1000: number): number {
  if (!Number.isFinite(views) || !Number.isFinite(ratePer1000)) return 0
  // Manfiy prosmotr — ma'lumot xatosi. Pulni manfiy qilib qaytarish o'rniga 0 beramiz,
  // aks holda xato balansdan pul "yeb" ketadi.
  if (views <= 0 || ratePer1000 <= 0) return 0
  return Math.floor((views * ratePer1000) / 1000)
}

/**
 * Noma'lum JSON qiymatni (masalan `Video.rateTiers` yoki `Setting.value`ni parse qilgan natija)
 * yaroqli `RateTier[]`ga aylantiradi — biror element yaroqsiz yoki qator bo'sh bo'lsa `null`
 * qaytaradi (chaqiruvchi tekis stavkaga tushadi). YAGONA validator: server (`settings.ts`) ham,
 * WebApp ham shu funksiyani ishlatadi. `RateTier` shakli — `{ views >= 0, ratePer1000 > 0 }` butun.
 */
export function coerceRateTiers(value: unknown): { views: number; ratePer1000: number }[] | null {
  if (!Array.isArray(value) || value.length === 0) return null
  const tiers: { views: number; ratePer1000: number }[] = []
  for (const item of value) {
    if (typeof item !== 'object' || item === null) return null
    const views = (item as { views?: unknown }).views
    const rate = (item as { ratePer1000?: unknown }).ratePer1000
    if (typeof views !== 'number' || !Number.isInteger(views) || views < 0) return null
    if (typeof rate !== 'number' || !Number.isInteger(rate) || rate <= 0) return null
    tiers.push({ views, ratePer1000: rate })
  }
  return tiers
}

/**
 * Bosqichli (zinapoyali) stavka bilan video summasini hisoblaydi. Har bosqich `views` (eni)
 * gacha bo'lgan prosmotrga o'z `ratePer1000` stavkasini qo'llaydi; keyingi bosqich undan
 * keyingi prosmotrga o'tadi. OXIRGI bosqich "qolgan barchasi" — uning `views` eni e'tiborsiz,
 * qolgan hamma prosmotrni qamrab oladi.
 *
 * Misol: bosqichlar [{30000, 10000}, {50000, 7000}, {∞, 4000}], views=60000 →
 * 30000×10000/1000 + 30000×7000/1000 = 300000 + 210000 = 510000.
 *
 * ⚠️ Har bosqich `calculateAmount()` (pul hisobining yagona yadrosi) orqali hisoblanadi —
 * "ko'paytir→bo'l" aniqligi har bandda saqlanadi. Bandlar bo'yicha yig'indi olinadi, ya'ni
 * band chegaralarida yaxlitlash bandma-band bo'ladi (butun so'm printsipi buzilmaydi).
 * Bitta bosqich = eski tekis `calculateAmount(views, tier.ratePer1000)` bilan bir xil.
 */
export function calculateTieredAmount(
  views: number,
  tiers: readonly { views: number; ratePer1000: number }[],
): number {
  if (!Number.isFinite(views) || views <= 0 || tiers.length === 0) return 0
  let remaining = Math.floor(views)
  let total = 0
  for (let i = 0; i < tiers.length; i++) {
    if (remaining <= 0) break
    const tier = tiers[i]!
    const isLast = i === tiers.length - 1
    // Oxirgi bosqich — qolgan barchasi; aks holda bosqich eni (yoki qolgani, qaysi kichik).
    const band = isLast ? remaining : Math.min(remaining, Math.max(0, Math.floor(tier.views)))
    total += calculateAmount(band, tier.ratePer1000)
    remaining -= band
  }
  return total
}
